// Promise polyfill
import "core-js/features/promise";
import { render } from "react-dom";
import { BrowserRouter } from "react-router-dom";
import axios, { AxiosError, AxiosRequestConfig } from "axios";
import App from "./components/App";
import "@amzn/awsui-global-styles/polaris.css";
import "@amzn/awsui-global-styles/one-theme.css";
import "./globalStyles.scss";
import {
  forceRefreshMidwayJwtToken,
  getMidwayJwtToken,
} from "./auth/MidwayJwtToken";
import {
  getAppSetting,
  IAppSettings,
  initializeAppSettings,
} from "./config/AppSettings";
import { applyLuscaTheme } from "./config/theme";
import { withDevUser } from "./helpers/devUser";

applyLuscaTheme();

// Add Authorization header to all axios calls
axios.interceptors.request.use(async (config) => {
  const jwtToken = await getMidwayJwtToken();
  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, no-param-reassign
  config.headers.Authorization = `Bearer ${jwtToken}`;
  // eslint-disable-next-line no-param-reassign
  config.url = withDevUser(
    config.url,
    getAppSetting("apiUrl") as string | undefined,
    window.location.search
  );
  return config;
});

// A 401/403 after a stale tab or a dropped connection usually means the cached
// Midway token expired: fetch a fresh one and retry the request once.
type RetriableConfig = AxiosRequestConfig & { luscaAuthRetried?: boolean };
axios.interceptors.response.use(undefined, async (error: unknown) => {
  const axiosError = error as AxiosError | undefined;
  const status = axiosError?.response?.status;
  const config = axiosError?.config as RetriableConfig | undefined;
  if (
    (status === 401 || status === 403) &&
    config &&
    !config.luscaAuthRetried
  ) {
    try {
      const jwtToken = await forceRefreshMidwayJwtToken();
      config.luscaAuthRetried = true;
      config.headers = {
        ...(config.headers as Record<string, string> | undefined),
        Authorization: `Bearer ${jwtToken}`,
      };
      return await axios(config);
    } catch {
      // fall through to the original error
    }
  }
  return Promise.reject(error);
});

(async () => {
  // Make sure Midway is present before rendering
  await getMidwayJwtToken();

  // Initialize application settings
  const appSettings = (await axios("/settings.json")).data as IAppSettings;
  initializeAppSettings(appSettings);

  render(
    <BrowserRouter>
      <App />
    </BrowserRouter>,
    document.querySelector("#app")
  );
})().catch((error) => console.log(error));
